import { useEffect, useState } from 'react';

/**
 * How long an inspection has been the current engineer's responsibility.
 *
 * ── WHICH TIMESTAMP, AND WHY ────────────────────────────────────────────────
 * `Inspection.assignedAt`, and there is no other candidate: the schema has no
 * `claimedAt`. Claiming IS assignment here — `InspectionService.claim()` sets
 * `assignedAt: now` alongside `status: ASSIGNED`, and the assignment paths in
 * `EquipmentIntakeService` set the same field.
 *
 * What makes it the right one rather than merely the only one is `unclaim()`:
 * it sets `assignedAt: null` and disconnects the engineer. So the field marks
 * the start of the CURRENT responsibility, not the first ever — hand work back
 * and the clock is cleared; the next engineer's claim starts a new one. Nothing
 * here is derived, invented, or written back: this component only reads a
 * persisted value.
 *
 * A null `assignedAt` (an UNASSIGNED inspection) has no elapsed time to show,
 * so nothing renders. A malformed value is treated the same way — a wrong timer
 * is worse than no timer.
 *
 * ── WHY IT CANNOT RESET ─────────────────────────────────────────────────────
 * Elapsed time is computed from that stored timestamp on every tick, never
 * accumulated in state. A refresh, a re-mount, or navigating between sections
 * recomputes the same number. There is no counter to lose.
 */

/** Threshold boundaries, in hours. Inclusive lower bounds, per the spec. */
const ORANGE_AFTER_HOURS = 24;
const RED_AFTER_HOURS = 72;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

export type ElapsedLevel = 'green' | 'orange' | 'red';

/** `green` < 24h ≤ `orange` < 72h ≤ `red`. Exported for direct testing. */
export function levelForElapsed(ms: number): ElapsedLevel {
  const hours = ms / HOUR_MS;
  if (hours >= RED_AFTER_HOURS) return 'red';
  if (hours >= ORANGE_AFTER_HOURS) return 'orange';
  return 'green';
}

/**
 * `19h 44m` under a day, `1d 08h` beyond it — hours zero-padded in the day form
 * so the width does not jitter as the value changes.
 *
 * Deliberately not `elapsedSince()` from the Authorization Center: that renders
 * "3 Days 4 Hours", which is right for a table cell and too wide for a header
 * badge. That helper is left untouched because the Authorization Center uses it.
 */
export function formatElapsed(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / MINUTE_MS));
  const hours = Math.floor(totalMinutes / 60);
  if (hours < 24) return `${hours}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
  return `${Math.floor(hours / 24)}d ${String(hours % 24).padStart(2, '0')}h`;
}

/** Long form for the accessible name — screen readers should not read "1d 08h". */
export function describeElapsed(ms: number, level: ElapsedLevel): string {
  const totalMinutes = Math.max(0, Math.floor(ms / MINUTE_MS));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    parts.push(`${days} day${days === 1 ? '' : 's'}`);
    if (hours % 24) parts.push(`${hours % 24} hour${hours % 24 === 1 ? '' : 's'}`);
  } else {
    if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    if (minutes || !hours) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }
  const state =
    level === 'red'
      ? 'seriously overdue'
      : level === 'orange'
        ? 'becoming overdue'
        : 'within the normal response window';
  return `Assigned ${parts.join(' ')} ago — ${state}`;
}

/**
 * Recomputes elapsed time once a minute — the smallest unit displayed.
 *
 * Paused while the tab is hidden (a background tab has nobody reading it, and
 * browsers throttle the timer anyway), and recomputed immediately on return so
 * the value is correct rather than however far the throttled interval drifted.
 * The interval and the listener are both torn down on unmount, so leaving the
 * inspection leaves nothing behind.
 */
function useElapsed(startedAtMs: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startedAtMs === null) return;

    let interval: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      setNow(Date.now());
      interval = setInterval(() => setNow(Date.now()), MINUTE_MS);
    };
    const stop = () => {
      if (interval !== undefined) clearInterval(interval);
      interval = undefined;
    };
    const onVisibility = () => (document.hidden ? stop() : start());

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [startedAtMs]);

  return startedAtMs === null ? null : Math.max(0, now - startedAtMs);
}

/**
 * Tinted background, no border — deliberately.
 *
 * The border made this badge 22px against the adjacent status badge's 20px,
 * growing the header row by 2px. The neighbour it must match is built
 * `inline-flex items-center rounded-full py-0.5 text-xs font-medium` with a
 * tint and no border, so this one is too, and the heights agree by
 * construction: text-xs carries a 16px line-height, the 14px icon is shorter
 * so the flex row stays 16px, and py-0.5 adds 2px either side — 20px.
 *
 * Note the absence of `leading-none`: it would cut the text box to 12px and
 * leave the badge 18px, missing the target by 2px in the other direction.
 *
 * `whitespace-nowrap` guards the same 20px: squeezed into a narrow column the
 * label breaks between "12d" and "14h", the badge grows to 36px, and the parity
 * with the status badge is lost exactly where it matters most.
 */
const LEVEL_CLASS: Record<ElapsedLevel, string> = {
  green: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  orange: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  red: 'bg-destructive/10 text-destructive',
};

/**
 * Inline SVG marks — no image, no GIF, no animation library, no network request.
 * Motion is CSS only and stops under `prefers-reduced-motion` via the project's
 * existing `motion-reduce:` convention. `aria-hidden` throughout: the state is
 * announced by the wrapper's accessible name, never by the drawing or by colour.
 */
function ClockMark({ level }: { level: ElapsedLevel }) {
  // The hand sweeps once a minute — the same cadence as the value it accompanies.
  const spin =
    level === 'orange' ? 'animate-[spin_4s_linear_infinite]' : 'animate-[spin_8s_linear_infinite]';
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" aria-hidden="true" focusable="false">
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.9"
      />
      <g className={`origin-center ${spin} motion-reduce:animate-none`}>
        <line
          x1="8"
          y1="8"
          x2="8"
          y2="4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </g>
      <line
        x1="8"
        y1="8"
        x2="10.5"
        y2="9.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.7"
      />
    </svg>
  );
}

/**
 * A compact bomb whose FUSE is what burns.
 *
 * The first attempt pulsed a separate dot near the fuse, which read as a
 * generic blinking indicator rather than something alight. Here the fuse path
 * itself carries a dashed stroke whose offset animates, so the dashes travel
 * along the cord — the fuse is visibly burning down — and a flame at its tip
 * flickers in size and opacity.
 *
 * Only `stroke-dashoffset`, `opacity` and `transform` animate. None of those
 * triggers layout, so the badge cannot shift or reflow, and the whole mark is
 * two paths and two circles at 14px.
 *
 * `transform-box: fill-box` is required for the flame: without it an SVG
 * element scales about the viewport origin rather than its own centre, and the
 * flame would fly off the icon instead of flickering in place.
 */
/** Shared by the cord and the burn so the two strokes cannot drift apart. */
const FUSE_PATH = 'M10.6 4.4 Q12.4 3.1 12.9 1.7';

function BombMark() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" aria-hidden="true" focusable="false">
      {/* Body and the short neck the fuse leaves from. */}
      <circle cx="6.6" cy="10.6" r="4.4" fill="currentColor" />
      <rect
        x="8.7"
        y="4.9"
        width="2.5"
        height="1.8"
        rx="0.4"
        transform="rotate(45 8.7 4.9)"
        fill="currentColor"
      />

      {/* The fuse is two strokes on the same path. The lower one is a solid
          cord that never animates, so a short fuse is always plainly visible —
          including under reduced motion, where the burn is switched off. The
          upper one is the burn: a fine dash pattern travelling along the cord.
          The path is only ~3.6 units long, so the dash period has to stay well
          under that or a single dash covers the whole fuse and it reads as a
          blink instead of something moving. */}
      <path
        d={FUSE_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity="0.45"
      />
      <path
        d={FUSE_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeDasharray="1 1"
        className="animate-[fuse-burn_0.5s_linear_infinite] motion-reduce:animate-none"
      />

      {/* Flame at the fuse tip: a soft halo around a bright core. Kept small on
          purpose — a larger halo swamps a 14px icon and starts to look like a
          cartoon rather than a status mark. */}
      <g
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        className="animate-[fuse-spark_0.45s_ease-in-out_infinite] motion-reduce:animate-none"
      >
        <circle cx="13.1" cy="1.4" r="1.45" fill="currentColor" opacity="0.3" />
        <circle cx="13.1" cy="1.4" r="0.85" fill="currentColor" />
      </g>
    </svg>
  );
}

interface Props {
  /** `Inspection.assignedAt` — ISO string, or null when nobody holds the job. */
  assignedAt: string | null | undefined;
}

export function InspectionElapsedIndicator({ assignedAt }: Props) {
  const parsed = assignedAt ? new Date(assignedAt).getTime() : NaN;
  const startedAtMs = Number.isFinite(parsed) ? parsed : null;
  const elapsedMs = useElapsed(startedAtMs);

  // No timestamp, an unparseable one, or a future one: show nothing rather than
  // something wrong. A clock reading "0h 00m" on an unassigned job would be a
  // claim about the work that is not true.
  if (startedAtMs === null || elapsedMs === null || parsed > Date.now() + MINUTE_MS) return null;

  const level = levelForElapsed(elapsedMs);

  return (
    <span
      data-testid="inspection-elapsed-indicator"
      data-level={level}
      // Non-colour signalling: the icon differs by level (clock vs bomb), and the
      // accessible name states the elapsed time and what it means in words.
      role="status"
      aria-label={describeElapsed(elapsedMs, level)}
      title={describeElapsed(elapsedMs, level)}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${LEVEL_CLASS[level]}`}
    >
      {level === 'red' ? <BombMark /> : <ClockMark level={level} />}
      <span>{formatElapsed(elapsedMs)}</span>
    </span>
  );
}
